/* eslint-disable */
import React, { useState, useEffect, useContext, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from 'Images/save_icon.svg';
import ConfigContext from 'Shared/ConfigContext';
import { setProfile } from 'Shared/ConfigContext/Actions';
import useNetworkLoader from 'hooks/useNetworkLoader';
import { securePost } from 'config/apiConfig';
import { getProfile } from 'Shared/Header/services';
import toast from 'Utils/toast';
import IdentificationSection from './IdentificationSection';
import PasswordSection from './PasswordSection';
import ContactSection from './ContactSection';
import LegalSection from './LegalSection';
import DeliverySection from './DeliverySection';
import PaymentSection from './PaymentSection';
import ConfirmDialog from 'Shared/ConfirmDialog';
import SaveToast from './SaveToast';

/* ── Build state from profile ────────────────────────────────────── */
const buildStateFromProfile = (p) => ({
  identification: {
    codeClient: p?.code || '',
    intituleCompte: p?.name || '',
    telephone: p?.phone || '',
    email: p?.email || '',
    ville: p?.city || '',
    adresse: p?.address || '',
  },
  password: {
    motDePasseActuel: '',
    nouveauMotDePasse: '',
    confirmerMotDePasse: '',
  },
  legal: {
    ice: p?.ice || '',
    registreCommerce: p?.rc || '',
    patente: p?.patente || '',
    ville: p?.city || '',
  },
  livraison: {
    conditionLivraison: p?.expedition?.name || '',
    transports: (p?.expeditions || []).map((e) => ({
      id: e.id,
      expeditionId: e.expedition?.id || null,
      transportPrimaire: e.expedition?.name || '',
      typeTransport: e.type || '',
      primary: e.primary || false,
    })),
  },
  payment: {
    modePaiement: p?.payment?.name || '',
    echeancePm: p?.nbrJourEcheancePm || '',
    echeanceHuile: p?.nbrJourEcheanceH || '',
  },
});

const PHONE_TYPE_TO_LABEL = {
  CALL_AND_WHATSAPP: 'Whatsapp/Appel',
  WHATSAPP_ONLY: 'Whatsapp/Appel',
  CALL_ONLY: 'Téléphone',
};

const buildContactsFromProfile = (p) =>
  (p?.contacts || []).map((c) => ({
    id: c.id,
    nom: c.fullName || '',
    type: PHONE_TYPE_TO_LABEL[c.phones?.[0]?.type] || c.phones?.[0]?.type || '',
    telephone: c.phones?.[0]?.phone || '',
  }));

const NAV_ITEMS = [
  { key: 'identification',        label: 'Identification' },
  { key: 'mot-de-passe',          label: 'Mot de passe' },
  { key: 'contact',               label: 'Contact' },
  { key: 'informations-legales',  label: 'Informations légales' },
  { key: 'livraison',             label: 'Livraison' },
  { key: 'condition-de-paiement', label: 'Conditions de paiement' },
];

let _contactId = 100;
let _transportId = 100;

const DIALOG_CLOSED = { open: false, variant: null, meta: null };

const AccountSettings = () => {
  const { dispatch, state: { profile, expeditions } } = useContext(ConfigContext);
  const { callApi } = useNetworkLoader();
  const initRef = useRef(null);
  const initContactsRef = useRef(null);

  const [state, setState] = useState(() => buildStateFromProfile(profile));
  const [contacts, setContacts] = useState(() => buildContactsFromProfile(profile));
  const [activeSection, setActiveSection] = useState('identification');
  const [dialog, setDialog] = useState(DIALOG_CLOSED);
  const [showToast, setShowToast] = useState(false);

  /* Sync when profile loads */
  useEffect(() => {
    if (profile && Object.keys(profile).length > 0) {
      const s = buildStateFromProfile(profile);
      const c = buildContactsFromProfile(profile);
      setState(s);
      setContacts(c);
      initRef.current = JSON.stringify(s);
      initContactsRef.current = JSON.stringify(c);
    }
  }, [profile]);

  /* ── Dirty check ─────────────────────────────────────────────── */
  const isDirty =
    JSON.stringify(state) !== initRef.current ||
    JSON.stringify(contacts) !== initContactsRef.current;

  /* ── Warn on browser close/refresh ──────────────────────────── */
  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  /* ── Scroll-spy ──────────────────────────────────────────────── */
  useEffect(() => {
    const sections = NAV_ITEMS.map((n) => document.getElementById(n.key)).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 },
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  /* ── Field handlers ──────────────────────────────────────────── */
  const mkHandler = (section) => (e) => {
    const { name, value } = e.target;
    setState((s) => ({ ...s, [section]: { ...s[section], [name]: value } }));
  };

  /* ── Contact handlers ────────────────────────────────────────── */
  const handleContactChange = (id, field, value) =>
    setContacts((c) => c.map((item) => (item.id === id ? { ...item, [field]: value } : item)));

  const handleAddContact = () =>
    setContacts((c) => [...c, { id: ++_contactId, nom: '', type: '', telephone: '' }]);

  const handleDeleteContact = (id) =>
    setContacts((c) => c.filter((item) => item.id !== id));

  /* ── Transport handlers ──────────────────────────────────────── */
  const handleTransportChange = (id, field, value) =>
    setState((s) => ({
      ...s,
      livraison: {
        ...s.livraison,
        transports: s.livraison.transports.map((t) =>
          t.id === id ? { ...t, [field]: value } : t,
        ),
      },
    }));

  const handleAddTransport = () =>
    setState((s) => ({
      ...s,
      livraison: {
        ...s.livraison,
        transports: [
          ...s.livraison.transports,
          { id: ++_transportId, expeditionId: null, transportPrimaire: '', typeTransport: '', primary: false },
        ],
      },
    }));

  const handleDeleteTransport = (id) =>
    setState((s) => ({
      ...s,
      livraison: {
        ...s.livraison,
        transports: s.livraison.transports.filter((t) => t.id !== id),
      },
    }));

  /* ── Delete dialog ───────────────────────────────────────────── */
  const confirmDelete = () => {
    const { type, id } = dialog.meta;
    if (type === 'contact') handleDeleteContact(id);
    else handleDeleteTransport(id);
    setDialog(DIALOG_CLOSED);
  };

  /* ── Save dialog ─────────────────────────────────────────────── */
  const requestSave = () => setDialog({ open: true, variant: 'save', meta: null });

  const confirmSave = async () => {
    /* Validate passwords before firing any API call */
    if (state.password.motDePasseActuel && state.password.nouveauMotDePasse) {
      if (state.password.nouveauMotDePasse !== state.password.confirmerMotDePasse) {
        toast.error('Les mots de passe ne correspondent pas');
        return;
      }
    }

    const payload = {
      name: state.identification.intituleCompte,
      email: state.identification.email,
      phone: state.identification.telephone,
      city: state.identification.ville,
      address: state.identification.adresse,
      ice: state.legal.ice,
      rc: state.legal.registreCommerce,
      patente: state.legal.patente,
      contacts: contacts.map((c) => {
        const phoneTypeMap = {
          'Whatsapp/Appel': 'CALL_AND_WHATSAPP',
          'Téléphone': 'CALL_ONLY',
        };
        return {
          id: c.id > 99 ? null : c.id,
          fullName: c.nom,
          phones: c.telephone ? [{ phone: c.telephone, type: phoneTypeMap[c.type] || 'CALL_ONLY' }] : [],
          primary: false,
        };
      }),
      expeditions: state.livraison.transports.filter((t) => t.expeditionId).map((t) => ({
        id: t.id > 99 ? null : t.id,
        expedition: t.expeditionId ? { id: t.expeditionId } : null,
        type: t.typeTransport || null,
        primary: t.primary || false,
      })),
    };

    const saves = [
      callApi(
        () => securePost('/users/updateProfile', payload),
        () => {
          setShowToast(true);
          callApi(() => getProfile(), (data) => { dispatch(setProfile(data)); }, false);
        },
      ),
    ];

    if (state.password.motDePasseActuel && state.password.nouveauMotDePasse) {
      saves.push(
        callApi(
          () => securePost('/users/password/change', {
            oldPassword: state.password.motDePasseActuel,
            newPassword: state.password.nouveauMotDePasse,
          }),
          () => {
            setState((s) => ({ ...s, password: { motDePasseActuel: '', nouveauMotDePasse: '', confirmerMotDePasse: '' } }));
            toast.success('Mot de passe modifié avec succès');
          },
        ),
      );
    }

    return Promise.all(saves);
  };

  /* ── Leave dialog ────────────────────────────────────────────── */
  const requestLeave = () => {
    if (isDirty) setDialog({ open: true, variant: 'leave', meta: null });
    else doCancel();
  };

  const doCancel = () => {
    setState(buildStateFromProfile(profile));
    setContacts(buildContactsFromProfile(profile));
    setDialog(DIALOG_CLOSED);
  };

  /* ── Dialog config ───────────────────────────────────────────── */
  const dialogConfig = {
    delete: {
      title: 'Supprimer cet élément',
      message: 'Cette action supprimera définitivement cet élément. Souhaitez-vous continuer ?',
      onConfirm: confirmDelete,
    },
    save: {
      title: 'Enregistrer les modifications',
      message: 'Cette action sauvegardera vos modifications. Souhaitez-vous continuer ?',
      onConfirm: confirmSave,
      confirmLabel: 'Confirmer',
    },
    leave: {
      title: 'Modifications non sauvegardées',
      message: 'Vous avez des modifications non sauvegardées. Souhaitez-vous quitter sans sauvegarder ?',
      onConfirm: doCancel,
      confirmLabel: 'Quitter',
    },
  };

  const activeDialog = dialog.variant ? dialogConfig[dialog.variant] : null;

  return (
    <Box sx={{ backgroundColor: '#F8FAFC', pt: '90px', px: { xs: '16px', md: '40px' }, pb: '40px', mt: '-80px', fontFamily: "'Open Sans', sans-serif !important", '& *': { fontFamily: "'Open Sans', sans-serif !important" } }}>

      {/* ── Title bar ─────────────────────────────────────────────── */}
      <Box
        sx={{
          mb: '16px',
          mt: '8px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#000000' }}>
            Paramètres du compte
          </Typography>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#385576' }}>
            Gérez vos informations personnelles
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Button
            onClick={requestLeave}
            disabled={!isDirty}
            startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
            sx={{
              backgroundColor: '#FFFFFF',
              boxShadow: '0px 5px 10px #626B7D12',
              borderRadius: '8px',
              color: '#385576',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              px: '18px',
              py: '10px',
              '&:hover': { backgroundColor: '#F0F2F5', boxShadow: '0px 5px 10px #626B7D12' },
              '& .MuiButton-startIcon': { marginRight: '1px' },
            }}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={requestSave}
            disabled={!isDirty}
            startIcon={<img src={SaveIcon} alt="" style={{ width: 16, height: 16, filter: 'brightness(0) invert(1)' }} />}
            sx={{
              backgroundColor: '#007BC6',
              color: '#fff',
              fontWeight: 600,
              fontSize: '14px',
              textTransform: 'none',
              borderRadius: '8px',
              px: '20px',
              py: '9px',
              boxShadow: '0px 2px 8px rgba(0,123,198,0.30)',
              '&:hover': { backgroundColor: '#0069A8', boxShadow: '0px 4px 12px rgba(0,123,198,0.35)' },
              '&.Mui-disabled': { backgroundColor: '#B0C4D8', color: '#fff', boxShadow: 'none' },
            }}
          >
            Enregistrer
          </Button>
        </Box>
      </Box>

      {/* ── Main layout: sidebar + sections ───────────────────────── */}
      <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>

        {/* ── Left sidebar ─────────────────────────────────────────── */}
        <Box
          sx={{
            width: '260px',
            flexShrink: 0,
            borderRadius: '12px',
            pt: 0,
            pb: '10px',
            position: 'sticky',
            top: '90px',
          }}
        >
          {NAV_ITEMS.map((item) => {
            const isActive = activeSection === item.key;
            return (
              <Box
                key={item.key}
                onClick={() => scrollTo(item.key)}
                sx={{
                  px: '14px',
                  py: '9px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  mb: '2px',
                  backgroundColor: isActive ? 'rgba(0, 123, 198, 0.07)' : 'transparent',
                  color: isActive ? '#007BC6' : '#4D4D4D',
                  fontWeight: isActive ? 700 : 600,
                  fontSize: '16px',
                  transition: 'all 0.15s',
                  userSelect: 'none',
                  '&:hover': !isActive
                    ? { backgroundColor: '#EBF5FF', color: '#007BC6' }
                    : {},
                }}
              >
                {item.label}
              </Box>
            );
          })}
        </Box>

        {/* ── Right: all sections ───────────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <IdentificationSection data={state.identification} onChange={mkHandler('identification')} />
          <PasswordSection data={state.password} onChange={mkHandler('password')} />
          <ContactSection
            contacts={contacts}
            onChangeContact={handleContactChange}
            onAddContact={handleAddContact}
            onDeleteContact={(id) => handleDeleteContact(id)}
          />
          <LegalSection data={state.legal} onChange={mkHandler('legal')} />
          <DeliverySection
            data={state.livraison}
            expeditions={expeditions || []}
            onChangeTransport={handleTransportChange}
            onAddTransport={handleAddTransport}
            onDeleteTransport={(id) => handleDeleteTransport(id)}
          />
          <PaymentSection data={state.payment} />
        </Box>
      </Box>

      {/* ── Dialogs ──────────────────────────────────────────────── */}
      {activeDialog && (
        <ConfirmDialog
          open={dialog.open}
          variant={dialog.variant}
          title={activeDialog.title}
          message={activeDialog.message}
          confirmLabel={activeDialog.confirmLabel}
          onClose={() => setDialog(DIALOG_CLOSED)}
          onConfirm={activeDialog.onConfirm}
        />
      )}

      {/* ── Toast ────────────────────────────────────────────────── */}
      <SaveToast open={showToast} onClose={() => setShowToast(false)} />
    </Box>
  );
};

export default AccountSettings;
